import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { AlertTriangle, BadgeDollarSign, Boxes, Clock, Package, Plus, Tag, Trash2, UserRound, Wrench } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/layout/PageHeader';
import { MetricCard } from '@/components/financials/MetricCard';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { DataTable, arrIncludesSome, type FacetedFilter } from '@/components/ui/DataTable';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/Dialog';
import { Skeleton } from '@/components/ui/Skeleton';
import { AddScrapDialog } from '@/components/manufacturing/AddScrapDialog';
import { ScrapLogSheet } from '@/components/manufacturing/ScrapLogSheet';
import { StoredImage } from '@/components/production/shared';
import { useAuth } from '@/app/AuthProvider';
import { canWrite } from '@/lib/permissions';
import { describeWorkError, formatDuration } from '@/features/workMode/api';
import { DEFECT_STATUS_LABEL, DEFECT_STATUS_VARIANT } from '@/features/workMode/labels';
import type { WorkDefect, WorkDefectStatus } from '@/features/workMode/types';
import { useWorkLookups } from '@/features/workMode/useLookups';
import { scrapLogCents, useAcknowledgeScrapAlert, useScrapAlerts, useScrapLogs, useScrapSummary } from '@/features/scrap/hooks';
import { SCRAP_ALERT_KIND_LABEL, type ScrapAlert } from '@/features/scrap/types';
import { scrapTagLabel } from '@/features/scrap/tags';
import { formatCurrency, formatDateTime, formatPercent, formatRelative } from '@/lib/formatters';
import { cn } from '@/lib/utils';

type Filter = 'all' | WorkDefectStatus | 'rework';

const FILTERS: Array<{ id: Filter; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'open', label: 'Open' },
  { id: 'rework', label: 'Needs rework' },
  { id: 'reviewed', label: 'Reviewed' },
  { id: 'resolved', label: 'Resolved' },
];

function facetOptions(values: Array<string | null | undefined>) {
  return Array.from(new Set(values.filter((v): v is string => !!v)))
    .sort((a, b) => a.localeCompare(b))
    .map((v) => ({ value: v, label: v }));
}

/**
 * Manufacturing → Scrap log. Scrap recorded here or from Work Mode, the dollars
 * and rate behind them, and alerts when a person, part, type, or component
 * is failing far enough from the rest of production to change.
 */
export function ScrapLogPage() {
  const { role } = useAuth();
  const canManage = canWrite(role, 'manufacturing');
  const lookups = useWorkLookups();
  const { data: summary, isLoading: summaryLoading, isError: summaryError } = useScrapSummary(30);
  const { data: alerts = [], isLoading: alertsLoading, isError: alertsError } = useScrapAlerts(30);
  const { data: logs = [], isLoading, isError: logsError } = useScrapLogs();
  const [filter, setFilter] = useState<Filter>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lightbox, setLightbox] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  const rows = useMemo(() => {
    return logs.filter((d) => {
      if (filter === 'rework') return d.needs_rework;
      if (filter === 'all') return true;
      return d.status === filter;
    });
  }, [logs, filter]);

  const selected = logs.find((r) => r.id === selectedId) ?? null;
  const days = summary?.window_days ?? 30;

  const columns = useMemo<ColumnDef<WorkDefect>[]>(() => {
    return [
      {
        id: 'created_at',
        accessorKey: 'created_at',
        header: 'When',
        size: 160,
        minSize: 120,
        cell: ({ row }) => <span className="whitespace-nowrap text-muted-foreground">{formatDateTime(row.original.created_at)}</span>,
      },
      {
        id: 'person',
        accessorFn: (r) => lookups.personName(r.user_id),
        header: 'Who',
        size: 150,
        minSize: 110,
        filterFn: arrIncludesSome,
        cell: ({ row }) => <span className="font-medium">{lookups.personName(row.original.user_id)}</span>,
      },
      {
        id: 'part',
        accessorFn: (r) => (r.item_id ? `${lookups.itemSku(r.item_id) ?? ''} ${lookups.itemName(r.item_id) ?? ''}`.trim() : ''),
        header: 'Part',
        size: 200,
        minSize: 140,
        filterFn: arrIncludesSome,
        cell: ({ row }) =>
          row.original.item_id ? (
            <span>
              <span className="font-mono text-xs text-muted-foreground">{lookups.itemSku(row.original.item_id)}</span>{' '}
              {lookups.itemName(row.original.item_id)}
            </span>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        id: 'tags',
        accessorFn: (r) => `${(r.scrap_tags ?? []).map(scrapTagLabel).join(', ')} ${r.description}`,
        header: 'Type',
        size: 180,
        minSize: 120,
        cell: ({ row }) => {
          const tags = row.original.scrap_tags ?? [];
          if (tags.length === 0) return <span className="text-muted-foreground">Untagged</span>;
          return (
            <span className="flex flex-wrap gap-1">
              {tags.slice(0, 2).map((t) => (
                <Badge key={t} variant="outline">
                  {scrapTagLabel(t)}
                </Badge>
              ))}
              {tags.length > 2 && <span className="text-xs text-muted-foreground">+{tags.length - 2}</span>}
            </span>
          );
        },
      },
      {
        id: 'rework',
        accessorFn: (r) => (r.needs_rework ? 'Needs rework' : ''),
        header: 'Rework',
        size: 110,
        minSize: 90,
        cell: ({ row }) =>
          row.original.needs_rework ? <Badge variant="warning-soft">Rework</Badge> : <span className="text-muted-foreground">—</span>,
      },
      {
        id: 'time',
        accessorFn: (r) => r.committed_seconds ?? 0,
        header: () => <span className="block text-right">Time on part</span>,
        size: 120,
        minSize: 90,
        cell: ({ row }) => <span className="block text-right tabular-nums">{formatDuration(row.original.committed_seconds)}</span>,
      },
      {
        id: 'thrown',
        accessorFn: (r) => (r.scrapped_parts ?? []).map((p) => `${p.qty} ${p.name}`).join(', '),
        header: 'Thrown out',
        size: 200,
        minSize: 120,
        cell: ({ row }) => {
          const parts = row.original.scrapped_parts ?? [];
          if (parts.length === 0) return <span className="text-muted-foreground">—</span>;
          return <span className="line-clamp-2 text-sm">{parts.map((p) => `${p.qty}× ${p.name}`).join(', ')}</span>;
        },
      },
      {
        id: 'value',
        accessorFn: (r) => scrapLogCents(r),
        header: () => <span className="block text-right">Value</span>,
        size: 110,
        minSize: 80,
        cell: ({ row }) => <span className="block text-right tabular-nums">{formatCurrency(scrapLogCents(row.original))}</span>,
      },
      {
        id: 'status',
        accessorFn: (r) => DEFECT_STATUS_LABEL[r.status],
        header: 'Status',
        size: 110,
        minSize: 90,
        filterFn: arrIncludesSome,
        cell: ({ row }) => <Badge variant={DEFECT_STATUS_VARIANT[row.original.status]}>{DEFECT_STATUS_LABEL[row.original.status]}</Badge>,
      },
    ];
  }, [lookups]);

  const facetedFilters: FacetedFilter[] = useMemo(
    () => [
      { columnId: 'person', title: 'Who', options: facetOptions(rows.map((r) => lookups.personName(r.user_id))) },
      { columnId: 'part', title: 'Part', options: facetOptions(rows.map((r) => (r.item_id ? lookups.itemName(r.item_id) : null))) },
      { columnId: 'status', title: 'Status', options: facetOptions(rows.map((r) => DEFECT_STATUS_LABEL[r.status])) },
    ],
    [rows, lookups],
  );

  const employee = summary?.top_employee;
  const part = summary?.top_part;
  const tag = summary?.top_tag;
  const component = summary?.top_component;

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Manufacturing / Scrap log"
        title="Scrap log"
        actions={
          <>
            {canManage && (
              <Button onClick={() => setAddOpen(true)}>
                <Plus className="h-4 w-4" />
                Log scrap
              </Button>
            )}
            <Button asChild variant="outline">
              <Link to="/work">
                <Trash2 className="h-4 w-4" />
                Open Work Mode
              </Link>
            </Button>
          </>
        }
      />

      {summaryError && (
        <p className="text-sm text-fresh-salmon-dark">Couldn't load scrap statistics. The log below is still the list of submissions.</p>
      )}
      {alertsError && <p className="text-sm text-fresh-salmon-dark">Couldn't load scrap alerts.</p>}

      <ScrapAlerts alerts={alerts} loading={alertsLoading} canManage={canManage} />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          label={`Scrap dollars (${days}d)`}
          value={formatCurrency(summary?.total_cents ?? 0, { whole: (summary?.total_cents ?? 0) >= 10000 })}
          hint={dollarHint(summary)}
          loading={summaryLoading}
          icon={<BadgeDollarSign className="h-4 w-4" />}
          tone="negative"
        />
        <MetricCard
          label={`Scrap rate (${days}d)`}
          value={summary?.scrap_rate == null ? '—' : formatPercent(summary.scrap_rate)}
          hint={
            summary
              ? `${summary.scrap_units.toLocaleString()} scrapped · ${summary.good_units.toLocaleString()} good units`
              : undefined
          }
          loading={summaryLoading}
          icon={<Trash2 className="h-4 w-4" />}
          tone={summary && summary.scrap_units > 0 ? 'negative' : 'neutral'}
        />
        <MetricCard
          label="Employee with most scrap"
          value={employee ? lookups.personName(employee.user_id) : '—'}
          hint={employee ? `${employee.scrap_units.toLocaleString()} units · ${formatCurrency(employee.cents)}` : `No scrap in ${days} days`}
          loading={summaryLoading}
          icon={<UserRound className="h-4 w-4" />}
          className="[&_.text-2xl]:truncate [&_.text-2xl]:text-xl"
        />
        <MetricCard
          label="Part with most scrap"
          value={part ? (lookups.itemName(part.item_id) ?? '—') : '—'}
          hint={
            part
              ? `${lookups.itemSku(part.item_id) ?? ''} · ${part.scrap_units.toLocaleString()} units · ${formatCurrency(part.cents)}`.replace(/^ · /, '')
              : `No scrapped part in ${days} days`
          }
          loading={summaryLoading}
          icon={<Package className="h-4 w-4" />}
          className="[&_.text-2xl]:truncate [&_.text-2xl]:text-xl"
        />
        <MetricCard
          label="Time already spent"
          value={formatDuration(summary?.committed_seconds ?? 0)}
          loading={summaryLoading}
          icon={<Clock className="h-4 w-4" />}
        />
        <MetricCard
          label="Most common type"
          value={tag ? scrapTagLabel(tag.tag) : '—'}
          hint={tag ? `${tag.count.toLocaleString()} of ${summary?.scrap_logs.toLocaleString() ?? 0} logs` : 'No tags yet'}
          loading={summaryLoading}
          icon={<Tag className="h-4 w-4" />}
          className="[&_.text-2xl]:truncate [&_.text-2xl]:text-xl"
        />
        <MetricCard
          label="Needs rework"
          value={(summary?.rework_count ?? 0).toLocaleString()}
          hint={
            summary && summary.scrap_logs > 0
              ? `${Math.round((summary.rework_count / summary.scrap_logs) * 100)}% of scrap logs`
              : 'Nothing flagged for rework'
          }
          loading={summaryLoading}
          icon={<Wrench className="h-4 w-4" />}
        />
        <MetricCard
          label="Top component thrown out"
          value={component ? component.name : '—'}
          hint={
            component
              ? `${component.sku ? `${component.sku} · ` : ''}${trimQty(component.qty)} pcs · ${formatCurrency(component.cents)}`
              : 'No components recorded'
          }
          loading={summaryLoading}
          icon={<Boxes className="h-4 w-4" />}
          className="[&_.text-2xl]:truncate [&_.text-2xl]:text-xl"
        />
      </div>

      <div className="flex flex-wrap gap-1.5">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setFilter(f.id)}
            className={cn(
              'rounded-full border px-3 py-1 text-sm transition-colors',
              filter === f.id ? 'border-info bg-info-soft/30 text-info' : 'border-border text-muted-foreground hover:bg-secondary',
            )}
          >
            {f.label}
          </button>
        ))}
      </div>

      <Card>
        <CardContent className="pt-6">
          <DataTable<WorkDefect, unknown>
            data={rows}
            columns={columns}
            isLoading={isLoading}
            getRowId={(r) => r.id}
            onRowClick={(r) => setSelectedId(r.id)}
            searchPlaceholder="Search by person, part, type, or what went wrong…"
            facetedFilters={facetedFilters}
            initialPageSize={25}
            pageSizes={[25, 50, 100]}
            tableId="manufacturing-scrap-log"
            enableColumnResizing
            mobileCard={{
              title: (r) => (r.item_id ? lookups.itemName(r.item_id) ?? 'Scrap' : 'Scrap'),
              subtitle: (r) => lookups.personName(r.user_id),
              badges: (r) => [
                <Badge key="status" variant={DEFECT_STATUS_VARIANT[r.status]}>
                  {DEFECT_STATUS_LABEL[r.status]}
                </Badge>,
                r.needs_rework ? (
                  <Badge key="rework" variant="warning-soft">
                    Rework
                  </Badge>
                ) : null,
                ...(r.scrap_tags ?? []).slice(0, 2).map((t) => (
                  <Badge key={t} variant="outline">
                    {scrapTagLabel(t)}
                  </Badge>
                )),
              ],
              amount: (r) => ({
                value: formatCurrency(scrapLogCents(r)),
                tone: 'negative' as const,
                caption: formatDuration(r.committed_seconds),
              }),
              meta: (r) => [
                { label: 'When', value: formatDateTime(r.created_at) },
                {
                  label: 'Thrown out',
                  value: (r.scrapped_parts ?? []).map((p) => `${p.qty}× ${p.name}`).join(', ') || '—',
                },
              ],
              leading: (r) =>
                r.photo_paths?.[0] ? (
                  <StoredImage bucket="work-captures" path={r.photo_paths[0]} alt="" className="h-16 w-16" />
                ) : null,
            }}
            emptyState={
              <EmptyState
                icon={Trash2}
                title={logsError ? "Couldn't load the scrap log" : filter === 'all' ? 'No scrap logged yet' : 'Nothing in this view'}
                action={
                  canManage && filter === 'all' && !logsError ? (
                    <Button onClick={() => setAddOpen(true)}>
                      <Plus className="h-4 w-4" />
                      Log scrap
                    </Button>
                  ) : undefined
                }
              />
            }
          />
        </CardContent>
      </Card>

      <AddScrapDialog open={addOpen} onOpenChange={setAddOpen} lookups={lookups} />

      <ScrapLogSheet
        log={selected}
        lookups={lookups}
        canManage={canManage}
        onOpenChange={(open) => !open && setSelectedId(null)}
        onPhoto={setLightbox}
      />

      <Dialog open={!!lightbox} onOpenChange={(o) => !o && setLightbox(null)}>
        <DialogContent className="max-w-4xl p-2">
          <DialogTitle className="sr-only">Scrap photo</DialogTitle>
          {lightbox && <StoredImage bucket="work-captures" path={lightbox} alt="Scrap photo" className="max-h-[80vh] w-full object-contain" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function dollarHint(summary: ReturnType<typeof useScrapSummary>['data']): string | undefined {
  if (!summary) return undefined;
  const bits = [`Materials ${formatCurrency(summary.material_cents)}`, `labor ${formatCurrency(summary.labor_cents)}`];
  if (summary.labor_missing > 0) bits.push(`${summary.labor_missing} without a pay rate`);
  if (summary.uncosted_component_lines > 0) bits.push(`${summary.uncosted_component_lines} components without a standard cost`);
  return bits.join(' · ');
}

function trimQty(qty: number): string {
  return Number.isInteger(qty) ? qty.toLocaleString() : qty.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function ScrapAlerts({
  alerts,
  loading,
  canManage,
}: {
  alerts: ScrapAlert[];
  loading: boolean;
  canManage: boolean;
}) {
  const acknowledge = useAcknowledgeScrapAlert();

  return (
    <section className="space-y-3">
      <div>
        <h2 className="text-sm font-medium text-foreground">What needs to change</h2>
      </div>
      {loading ? (
        <Skeleton className="h-24 rounded-2xl" />
      ) : alerts.length === 0 ? (
        <p className="rounded-xl border border-dashed border-border px-4 py-6 text-sm text-muted-foreground">
          No anomalies right now.
        </p>
      ) : (
        <ul className="space-y-3">
          {alerts.map((a) => (
            <li
              key={a.id}
              className={cn(
                'rounded-2xl border bg-card p-4',
                a.status === 'open' ? 'border-fresh-salmon/70' : 'border-border',
              )}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex min-w-0 items-start gap-3">
                  <AlertTriangle className={cn('mt-0.5 h-5 w-5 shrink-0', a.status === 'open' ? 'text-fresh-salmon-dark' : 'text-muted-foreground')} />
                  <div className="min-w-0 space-y-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <Badge variant={a.status === 'open' ? 'warning-soft' : 'muted'}>{SCRAP_ALERT_KIND_LABEL[a.kind]}</Badge>
                      {a.status === 'acknowledged' && <Badge variant="outline">Acknowledged</Badge>}
                      <span className="text-sm font-medium text-foreground">{a.title}</span>
                    </div>
                    <p className="text-sm text-foreground">{a.detail}</p>
                    <p className="text-sm text-muted-foreground">{a.recommendation}</p>
                    <p className="text-xs text-muted-foreground">
                      {a.kind === 'employee' || a.kind === 'part'
                        ? `${(a.scrap_rate * 100).toFixed(1)}% vs ${(a.baseline_rate * 100).toFixed(1)}% comparison · ${a.z_score.toFixed(1)} standard errors`
                        : `${a.scrap_count.toLocaleString()} of ${a.sample_count.toLocaleString()}`}
                      {a.dollar_cents > 0 ? ` · ${formatCurrency(a.dollar_cents)}` : ''}
                      {` · since ${formatRelative(a.first_seen_at)}`}
                    </p>
                  </div>
                </div>
                {canManage && a.status === 'open' && (
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={acknowledge.isPending}
                    onClick={() =>
                      acknowledge.mutate(a.id, {
                        onError: (e) => toast.error(describeWorkError(e)),
                      })
                    }
                  >
                    Acknowledge
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
